import { Component } from 'react';
import { Button } from '@launchpad-ui/components';
import { Icon, type IconName } from '@launchpad-ui/icons';
import { format } from 'date-fns';

import { type Scopes } from '../../auth';
import { Alert, CircularIconButton, type CircularIconButtonIntent } from '../../shared';
import { AccountRole, MFAStatus } from '../model';

const statusToTooltip = {
  enabled: 'MFA is enabled for this member',
  disabled: 'MFA is disabled for this member',
  reset: 'This member used their recovery code and must reset their MFA settings',
};

export type MFADisplay = Readonly<{
  iconName: IconName;
  intent: CircularIconButtonIntent;
  tooltip: string;
}>;

export const getMFADisplay = (
  status: MFAStatus,
  mfaEnforced?: boolean,
  mfaGracePeriodExpiresAt?: number,
  externalAuthCategory?: string,
): MFADisplay => {
  // SSO/OAuth/Marketplace members — MFA managed by identity provider
  if (externalAuthCategory) {
    const intentMap: Record<string, CircularIconButtonIntent> = {
      SSO: 'info',
      OAuth: 'success',
      Marketplace: 'purple',
    };
    return {
      iconName: 'shield-key',
      intent: intentMap[externalAuthCategory] ?? 'info',
      tooltip: 'MFA is managed by the identity provider',
    };
  }

  if (status === MFAStatus.DISABLED && mfaEnforced) {
    if (
      mfaGracePeriodExpiresAt !== undefined &&
      Number.isFinite(mfaGracePeriodExpiresAt) &&
      mfaGracePeriodExpiresAt > Date.now()
    ) {
      return {
        iconName: 'clock-alert',
        intent: 'warning',
        tooltip: `MFA required - grace period expires ${format(mfaGracePeriodExpiresAt, 'MM/dd/yyyy')}`,
      };
    }
    return { iconName: 'cancel', intent: 'danger', tooltip: 'MFA required - blocked at login' };
  }

  switch (status) {
    case MFAStatus.ENABLED:
      return { iconName: 'check', intent: 'success', tooltip: statusToTooltip.enabled };
    case MFAStatus.DISABLED:
      return { iconName: 'cancel', intent: 'danger', tooltip: statusToTooltip.disabled };
    case MFAStatus.RESET:
      return { iconName: 'sync', intent: 'warning', tooltip: statusToTooltip.reset };
    default:
      return { iconName: 'info', intent: 'neutral', tooltip: '' };
  }
};

type MFAStatusTagProps = Readonly<{
  scopes: Scopes;
  status: MFAStatus;
  role: AccountRole;
  onMFADisable: () => void;
  enableMemberMfaReset?: boolean;
  mfaEnforced?: boolean;
  mfaGracePeriodExpiresAt?: number;
  externalAuthCategory?: string;
}>;

type MFAStatusTagState = Readonly<{
  alertIsOpen: boolean;
}>;

export class MFAStatusTag extends Component<MFAStatusTagProps, MFAStatusTagState> {
  public state: MFAStatusTagState = {
    alertIsOpen: false,
  };

  public render() {
    const { scopes, status, role, enableMemberMfaReset, mfaEnforced, mfaGracePeriodExpiresAt, externalAuthCategory } =
      this.props;
    const { alertIsOpen } = this.state;
    const { iconName, intent, tooltip } = getMFADisplay(
      status,
      mfaEnforced,
      mfaGracePeriodExpiresAt,
      externalAuthCategory,
    );

    return (
      <>
        <CircularIconButton
          iconName={iconName}
          intent={intent}
          size="medium"
          tooltip={tooltip}
          tooltipPlacement="top"
        />
        {/* Show legacy "Disable MFA" button only when the new kebab menu is NOT available.
            The kebab requires BOTH the flag AND the scope. If the user has the flag but not the
            scope (e.g., Billing team), they should still see this legacy button. */}
        {!(enableMemberMfaReset && scopes.memberMfaReset) &&
          scopes.ownerMfaReset &&
          role === AccountRole.OWNER &&
          (status === MFAStatus.ENABLED || status === MFAStatus.RESET) && (
            <>
              <Button className="mfaReset-Button" variant="destructive" size="small" onClick={this.handleDisable}>
                <Icon name="cancel" slot="start-icon" />
                Disable MFA
              </Button>
              <Alert
                intent="danger"
                isOpen={alertIsOpen}
                onClose={this.handleCancel}
                onCancel={this.handleCancel}
                onConfirm={this.handleConfirm}
                confirmButtonText="Disable"
                cancelButtonText="Cancel"
                title="Disable multi-factor authentication?"
                iconName="warning"
              >
                <p>This reduces the security of the account and is not recommended. Are you sure?</p>
              </Alert>
            </>
          )}
      </>
    );
  }

  private handleDisable = () => this.setState({ alertIsOpen: true });

  private handleCancel = () => this.setState({ alertIsOpen: false });

  private handleConfirm = () => {
    this.setState({ alertIsOpen: false });
    this.props.onMFADisable();
  };
}
